/**
 * Áudio procedural (Web Audio) — sem arquivos: efeitos e música sintetizados em código.
 * Respeita autoplay (o AudioContext só acorda após um gesto do usuário) e um mute persistente.
 * Tudo é no-op se o contexto não existir (SSR/teste sem gesto) — nunca lança.
 */
type Sfx = 'click' | 'shoot' | 'hit' | 'gem' | 'level' | 'special' | 'win' | 'lose';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let mudo = typeof localStorage !== 'undefined' && localStorage.getItem('rabbit.mute') === '1';
let musicaOn = false;
let musicTimer: ReturnType<typeof setInterval> | null = null;
let passo = 0;

function garantirCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = mudo ? 0 : 0.5;
    master.connect(ctx.destination);
  } catch {
    ctx = null;
  }
  return ctx;
}

/** acorda o áudio após um gesto (clique/tecla). Chamar de handlers de interação. */
export function acordarAudio(): void {
  const c = garantirCtx();
  if (c && c.state === 'suspended') void c.resume();
}

function tom(freq: number, dur: number, tipo: OscillatorType, vol: number, quando = 0): void {
  const c = ctx;
  if (!c || !master || mudo) return;
  const t = c.currentTime + quando;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = tipo;
  osc.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(g).connect(master);
  osc.start(t);
  osc.stop(t + dur + 0.02);
}

/** toca um efeito sonoro (no-op se mudo/sem contexto). */
export function sfx(nome: Sfx): void {
  if (!garantirCtx() || mudo) return;
  switch (nome) {
    case 'click': tom(520, 0.08, 'square', 0.2); break;
    case 'shoot': tom(880, 0.05, 'triangle', 0.08); break;
    case 'hit': tom(160, 0.07, 'sawtooth', 0.12); break;
    case 'gem': tom(660, 0.06, 'sine', 0.18); tom(990, 0.08, 'sine', 0.14, 0.05); break;
    case 'level': [523, 659, 784, 1047].forEach((f, i) => tom(f, 0.12, 'square', 0.16, i * 0.07)); break;
    case 'special': tom(220, 0.35, 'sawtooth', 0.22); tom(440, 0.35, 'sine', 0.16, 0.02); break;
    case 'win': [523, 659, 784, 1047, 1319].forEach((f, i) => tom(f, 0.18, 'triangle', 0.2, i * 0.12)); break;
    case 'lose': [392, 330, 262, 196].forEach((f, i) => tom(f, 0.22, 'sine', 0.2, i * 0.14)); break;
  }
}

// melodia de ninar em Lá menor pentatônica — loop suave (Moonlit Storybook)
const MELODIA = [440, 523, 659, 587, 523, 440, 392, 440, 659, 587, 523, 494];
function tick(): void {
  const c = ctx;
  if (!c || !master || mudo || !musicaOn) return;
  const nota = MELODIA[passo % MELODIA.length]!;
  tom(nota, 0.5, 'triangle', 0.06); // melodia
  if (passo % 4 === 0) tom(nota / 2, 0.7, 'sine', 0.05); // baixo
  passo += 1;
}

export function iniciarMusica(): void {
  if (musicaOn || !garantirCtx()) return;
  musicaOn = true;
  musicTimer = setInterval(tick, 380);
}

export function pararMusica(): void {
  musicaOn = false;
  if (musicTimer) { clearInterval(musicTimer); musicTimer = null; }
}

export function estaMudo(): boolean {
  return mudo;
}

/** alterna mudo (persistente). Retorna o novo estado. */
export function alternarMudo(): boolean {
  mudo = !mudo;
  if (typeof localStorage !== 'undefined') localStorage.setItem('rabbit.mute', mudo ? '1' : '0');
  if (master) master.gain.value = mudo ? 0 : 0.5;
  return mudo;
}
